/* eslint-disable class-methods-use-this */
import * as utils from 'Utils/elementUtils';
import {
    register as behaviorClickRegistered,
    unregister as behaviorClickUnregistered
} from 'Utils/behaviorClick';
import {explodeParamsString, implodeParamsObject} from 'Utils/urlUtils';
import {getTemplate} from 'Services/templates';
import {UAParserService} from 'Services/UAParserService';

const isIE = () => UAParserService.getBrowser().name === 'IE';
const hiddenClass = 'hidden';

export default class Paginator {
    /**
     * @description Constructor options.
     * @param {HTMLElement} domElement - Element which contains Paginator.
     * @param {object} [options] - List of options for Paginator.
     * @param {number} [options.nbItemsPerPage=9] - Numbers of items per page.
     * @param {number} [options.maxDisplayPager=5] - Maximum pages to skip.
     * @param {string} [options.masterPageType=HubEdito] - To load list tpl.
     * @param {string} [options.pager=Pager] - To load pager tpl.
     */
    constructor(domElement, options = {}) {
        this.options = {
            masterPageType: options.masterPageType || 'HubEdito',
            maxDisplayPager: options.maxDisplayPager || 5,
            nbItemsPerPage: options.nbItemsPerPage || 9,
            pager: options.pager || 'Pager'
        };

        this.checkBeforeInit(domElement);

        this.domElement = domElement;
        this.intersectionObserver = null;

        this.setDOMProps();
        this.setInfosProps();

        this.state = {
            categoryName: 'all',
            currentList: this.paginatorData,
            currentPage: 1,
            filteredList: this.paginatorData,
            numberOfPages: 1,
            pagerOffset: 0,
            totalItems: this.paginatorData.length,
            tpl: 'CONSEILS'
        };

        this.init();
    }

    /**
     * @description Check if all conditions are meet for the Paginator to work.
     * If the domElement that init the Paginator exists.
     * If the data attribute with the Json is here.
     * If there is wrapper to update.
     * If the Paginator is not already initialized on the domElement.
     *
     * @param {HTMLElement} domElement - The dom to check.
     */
    // eslint-disable-next-line complexity
    checkBeforeInit(domElement) {
        if(!domElement) {
            throw new Error('element is undefined');
        }

        if(!domElement.dataset.paginatorInfo || domElement.dataset.paginatorInfo === '') {
            throw new Error('no data-paginator-info data for update');
        }

        if(!domElement.querySelector('[data-paginator-wrapper]')) {
            throw new Error('data-paginator-wrapper attribute to locate the container to update');
        }

        if(domElement.dataset.paginatorInit === 'true') {
            throw new Error('paginator is already initialized');
        }
    }

    /**
     * @description Set all DOM element or list needed as class props.
     */
    setDOMProps() {
        this.domPagerList = null;
        this.domListWrapper = this.domElement.querySelector('[data-paginator-wrapper]');
        this.domCatWrapper = this.domElement.querySelector('[data-wrapper-cat]');
        this.domPagerWrapper = this.domElement.querySelector('[data-pager-wrapper]');
        this.domPagerListContainer = this.domElement.querySelector('[data-pager-list]');
        this.domCategoriesList = [...this.domElement.querySelectorAll('[data-category-name]')];
        this.domPaginatorTitleCounter = this.domElement.querySelector('[data-item-count]');
        this.domPagerListWrapper = this.domElement.querySelector('.pager__wrapper');
    }

    /**
     * @description Set all infos needed as class props.
     */
    setInfosProps() {
        this.pageType = this.domElement.getAttribute('data-paginator-tpl');
        this.nbItemsPerPage = this.options.nbItemsPerPage;
        this.maxDisplayPager = this.options.maxDisplayPager;
        this.pagerListWrapperPadding = 20;
        this.urlParams = explodeParamsString(window.location.search);
        this.isInfiniteScroll = this.domElement.dataset.infiniteScroll;
        this.paginatorData = JSON.parse(this.domElement.dataset.paginatorInfo);
    }

    init() {
        this.setLayoutModel();
        this.initTemplates();

        this.domElement.dataset.paginatorInit = 'true';

        if(this.isInfiniteScroll === 'true') {
            this.limitFilteredList();
        } else {
            this.initView();
        }

        this.initListener();
    }

    /**
     * @description Init Instance for Pager and Categories filter.
     * Add infos in Paginator props.
     */
    initTemplates() {
        if(!this.TemplateInstance) {
            this.TemplateInstance = getTemplate(this.options.masterPageType);
        }

        if(!this.PagerInstance) {
            this.PagerInstance = getTemplate(this.options.pager);
        }

        this.activeCategoryClassName = this.TemplateInstance.templateInfos.activeCategoryClassName;
        this.activePagerBulletClassName = this.PagerInstance.templateInfos.activePagerBulletClassName;
    }

    /**
     * @description Init view with Pager and categories filter.
     */
    initView() {
        this.updateStateFromParams();
        this.setStateCategoryFilterInfos(this.state.categoryName);

        this.updatePaginatorTitleCounter();
        this.updatePagerBulletList();
        this.updatePagination();

        this.updateCategoryItemClassName();
    }

    /**
     * @description Change the value of a state.
     * @param {object} valueObject - Value to change in store.
     */
    setState(valueObject) {
        this.state = {...this.state, ...valueObject};
    }

    /**
     * @description Filter data with the category name.
     * @returns {Array} - Array of data filtered.
     */
    getFilteredList() {
        if(this.state.categoryName !== 'all') {
            return this.paginatorData.filter(item => this.state.categoryName === item.tags[0]);
        }

        return this.paginatorData;
    }

    /**
     * @description When trigger it filter the list of data-paginator-info.
     * With the name of the categoryName and add it to the filteredList State.
     * It also set the state of the number of page and the total items and reset the currentPage to 1.
     * @param {string} [categoryName=all] - Category name.
     */
    setStateCategoryFilterInfos(categoryName = 'all') {
        this.setStateCategoryName(categoryName);

        const filteredList = this.getFilteredList();

        this.setState({
            filteredList,
            numberOfPages: Math.ceil(filteredList.length / this.nbItemsPerPage),
            totalItems: filteredList.length
        });
    }

    /**
     * @description Set state category name.
     * @param {string} categoryName - Category name.
     */
    setStateCategoryName(categoryName) {
        this.setState({categoryName});
    }

    /**
     * @description Update the page title with the category name if needed and the total number of items.
     */
    updatePaginatorTitleCounter() {
        this.domPaginatorTitleCounter.innerHTML = this.getCounterTitle()
            ? `${this.getCounterTitle()} - ${this.state.totalItems}`
            : this.state.totalItems;
    }

    /**
     * @description Get the counter title.
     * @returns {string} - Dom text content from item category if category name is not all.
     */
    getCounterTitle() {
        if(this.state.categoryName !== 'all') {
            return this.domElement.querySelector(`[data-category-name="${this.state.categoryName}"]`).innerHTML;
        }

        return '';
    }

    /**
     * @description Set state of currentList.
     * @param {Array} currentList - Current list in the view.
     */
    setStateCurrentList(currentList) {
        this.setState({currentList});
    }

    /**
     * @description Slice the filtered list and update currentList state.
     */
    limitFilteredList() {
        const lastIndex = this.nbItemsPerPage * this.state.currentPage;
        const firstIndex = lastIndex - this.nbItemsPerPage < 0 ? 0 : lastIndex - this.nbItemsPerPage;

        this.setStateCurrentList(this.state.filteredList.slice(firstIndex, lastIndex));
    }

    /**
     * @description Create a list of a number of items that we will use to append on the currentList.
     * @param {number} numberOfNewItems - Number of new items.
     */
    setStateAddToCurrent(numberOfNewItems) {
        const firstIndex = this.state.currentList.length;
        const newItems = this.state.filteredList.slice(firstIndex, firstIndex + numberOfNewItems);
        this.setState({addToCurrent: newItems});
    }

    /**
     * @description Add the addToCurrent state list to the currentList.
     */
    setStateCurrentListWithAddItem() {
        const currentList = [
            ...this.state.currentList,
            ...this.state.addToCurrent
        ];

        this.setStateCurrentList(currentList);
    }

    /**
     * @description Increment the current page by 1.
     */
    nextPage() {
        if(this.state.currentPage >= this.state.numberOfPages) {
            return;
        }

        this.setStateCurrentPage(this.state.currentPage + 1);
    }

    /**
     * @description Decrement the current page by 1.
     */
    previousPage() {
        if(this.state.currentPage === 1) {
            return;
        }

        this.setStateCurrentPage(this.state.currentPage - 1);
    }

    /**
     * @description If we directly target the number, the currentPage change.
     * @param {number} currentPage - The current page to change.
     */
    setStateCurrentPage(currentPage) {
        this.setState({currentPage});
    }

    /**
     * @description Generate the layout with the number of pages for the pager.
     * @returns {string} - The template generated.
     */
    layoutPagerBuilder() {
        let completeLayout = '';
        const dataToPopulateHTML = {currentPage: this.state.currentPage};
        const {numberOfPages} = this.state;

        for(let i = 0; i < numberOfPages; i++) {
            completeLayout += this.PagerInstance.getTplDOM(dataToPopulateHTML)(i + 1);
        }

        return completeLayout;
    }

    setPagerListWrapperWidthDelayed() {
        if(isIE()) {
            setTimeout(() => this.setPagerListWrapperWidth(), 1000);
        } else {
            this.setPagerListWrapperWidth();
        }
    }

    /**
     * @description Update the pager with the number of page and reset the listener on the items.
     * @param {boolean} [firstLoad=true] - Is the first load of the component.
     */
    updatePagerBulletList(firstLoad = true) {
        if(!this.domPagerWrapper) {
            return;
        }

        if(this.state.numberOfPages > 1) {
            if(!firstLoad) {
                this.domPagerListContainer.innerHTML = this.layoutPagerBuilder();
            }

            utils.removeClassesFromElement(this.domPagerWrapper, hiddenClass);

            this.setDomPagerList();
            this.setPagerListWrapperWidthDelayed();
            this.pagerListListenerManager(this.domPagerList, 'add');
        } else {
            utils.addClassesToElement(this.domPagerWrapper, hiddenClass);
        }
    }

    /**
     * @description Set DOM pager list from Pager.
     */
    setDomPagerList() {
        if(this.domPagerList) {
            this.pagerListListenerManager(this.domPagerList, 'remove');
        }

        this.domPagerList = [...this.domPagerListContainer.querySelectorAll('[data-page-nb]')];
    }

    /**
     * @description Set width to Pager wrapper.
     */
    setPagerListWrapperWidth() {
        const bulletCSSStyle = this.domPagerList[0].currentStyle
            || window.getComputedStyle(this.domPagerList[0]);

        // Warning: to get the good size for ie we need to have the margin in px and not in rem
        if(!this.bulletListWidth) {
            this.bulletListWidth = this.domPagerList[0].offsetWidth
                + Number.parseFloat(bulletCSSStyle.marginLeft)
                + Number.parseFloat(bulletCSSStyle.marginRight);
            this.offetsetItemWidth = this.domPagerListContainer.offsetWidth / this.domPagerList.length;
        }

        const pagerWrapperWidth = this.bulletListWidth * this.maxDisplayPager
            + this.pagerListWrapperPadding;
        const nbPagerItemsCheck = this.state.numberOfPages < this.maxDisplayPager ? 'auto' : `${pagerWrapperWidth}px`;

        this.domPagerListWrapper.style.width = `${nbPagerItemsCheck}`;
        this.domPagerListWrapper.style.flex = `0 0 ${nbPagerItemsCheck}`;
    }

    /**
     * @description Check which page we are and add the class on the current page.
     */
    updateBulletClassName() {
        const currentBullet = this.domPagerListContainer.querySelector(`[data-page-nb="${this.state.currentPage}"]`);
        utils.setUniqueClassName(
            currentBullet,
            this.domPagerListContainer,
            this.activePagerBulletClassName
        );
    }

    /**
     * @description Update Category Item active className.
     * @param {HTMLElement} currentTarget - CurrentTarget from event.
     */
    updateCategoryItemClassName(currentTarget) {
        const domCategoryItem = currentTarget
            || this.domCategoriesList.find(domCategory => domCategory.dataset.categoryName === this.state.categoryName);

        utils.setUniqueClassName(domCategoryItem, this.domCatWrapper, this.activeCategoryClassName);
    }

    /**
     * @description Get offset in px to slide pager.
     * @returns {number} - The pager offset.
     */
    getPagerOffset() {
        const averageBulletDisplay = Math.ceil(this.maxDisplayPager / 2);
        const isMaxDisplayPagerPair = this.maxDisplayPager % 2;
        const maxOffset = -(this.state.numberOfPages
            - (averageBulletDisplay + averageBulletDisplay - isMaxDisplayPagerPair)
        ) * this.offetsetItemWidth;
        const pagerOffset = (averageBulletDisplay * this.offetsetItemWidth)
            - (this.state.currentPage * this.offetsetItemWidth);

        if(pagerOffset > 0) {
            return 0;
        }
        if(pagerOffset < maxOffset) {
            return maxOffset;
        }

        return pagerOffset;
    }

    /**
     * @description Check of the current pager position.
     */
    updatePagerPosition() {
        if(this.state.numberOfPages < this.maxDisplayPager) {
            this.setState({pagerOffset: 0});
        } else {
            this.setState({pagerOffset: this.getPagerOffset()});
        }

        this.setPagerListContainerTranslate();
    }

    /**
     * @description Make the pager move to show the current page we are on.
     */
    setPagerListContainerTranslate() {
        this.domPagerListContainer.style.transform = `translateX(${this.state.pagerOffset}px)`;
    }

    /**
     * @description Check the data-paginator-tpl attribute and change the state tpl if necessary.
     */
    setLayoutModel() {
        if(!this.pageType) {
            return;
        }
        this.setState({tpl: this.pageType.toUpperCase()});
    }

    /**
     * @description Generate the template and add the info in it.
     * @param {object} data - Data to inject in template.
     * @returns {string} - The template.
     */
    layoutBuilder(data) {
        return this.TemplateInstance.getTplDOM({
            ...data,
            pageType: this.pageType
        }, this.state.tpl);
    }

    /**
     * @description Generate all the items that will append on the view.
     * @param {Array} list - List of something.
     * @returns {string} - Generated html.
     */
    generateListHTML(list) {
        return list.map(item => this.layoutBuilder(item))
            .reduce((current, data) => current + data, '');
    }

    /**
     * @description Generate or append content in the list wrapper.
     * @param {string} content - The content to inject.
     * @param {boolean} isToGenerate - True to generate instead of append content.
     */
    appendList(content, isToGenerate = true) {
        if(isToGenerate) {
            this.domListWrapper.innerHTML = content;
        } else {
            const listWrapper = document.createElement('div');
            listWrapper.innerHTML = content;

            const list = [...listWrapper.childNodes];

            list.forEach(item => this.domListWrapper.appendChild(item));
        }
    }

    processUpdateCategory(currentTarget, categoryName) {
        this.setStateCurrentPage(1);
        this.setStateCategoryFilterInfos(categoryName);
        this.limitFilteredList();
        this.updatePaginatorTitleCounter();
        this.updatePagerBulletList(false);
        this.updatePagination();
        this.updateCategoryItemClassName(currentTarget);
        this.render();
    }

    /**
     * @description Callback when clicking a category.
     * @param {Event} event - The params.
     * @param {HTMLElement} event.currentTarget - Dom currentTarget from click event.
     */
    updateCategory({currentTarget}) {
        const {categoryName} = currentTarget.dataset;

        if(categoryName === this.state.categoryName) {
            return;
        }

        this.processUpdateCategory(currentTarget, categoryName);
    }

    /**
     * @description Update the bullet position and the pager position.
     * Move the page if the condition are meet and rerender the view.
     */
    updatePagination() {
        this.hideFirstLastArrowPager();
        this.updateBulletClassName();
        this.updatePagerPosition();
    }

    /**
     * @description Is used to attach event when the pager number are generated.
     * @param {HTMLElement[]} list - List of pages.
     * @param {string} action - The action to perform.
     */
    pagerListListenerManager(list, action) {
        list.forEach(domItem => {
            domItem[`${action}EventListener`]('click', event => {
                if(action === 'add') {
                    event.preventDefault();
                }
                const pageNb = Number(event.currentTarget.dataset.pageNb);
                if(this.state.currentPage !== pageNb) {
                    this.setStateCurrentPage(pageNb);
                    this.limitFilteredList();
                    this.updatePagination();
                    this.render();
                }
            });
        });
    }

    /**
     * @description Build and render the list and update url params.
     */
    render() {
        if(this.isInfiniteScroll === 'true') {
            this.appendList(this.generateListHTML(this.state.addToCurrent), false);
            this.updateIntersectionObserver();
        } else {
            behaviorClickUnregistered();
            this.appendList(this.generateListHTML(this.state.currentList));
        }

        this.updateParamsFromState();
        this.updateCanonicalHref();
    }

    /**
     * @description Init Intersection Observer and callback for infinite scroll.
     */
    initIntersectionObserver() {
        if(!this.intersectionObserver) {
            this.intersectionObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if(entry.intersectionRatio > 0) {
                        this.setStateAddToCurrent(3);
                        this.setStateCurrentListWithAddItem();
                        this.render();
                        this.intersectionObserver.unobserve(entry.target);
                    }
                });
            });
        }
    }

    /**
     * @description Observe last element of the list or disconnect intersectionObserver.
     */
    updateIntersectionObserver() {
        const obsElementsList = [...this.domElement.querySelectorAll('[data-card-item]')];
        const domElementToObserve = obsElementsList[obsElementsList.length - 1];

        if(this.state.currentList.length < this.state.filteredList.length) {
            this.intersectionObserver.observe(domElementToObserve);
        } else {
            this.intersectionObserver.disconnect();
        }
    }

    /**
     * @description Update url params with specific values of state.
     */
    updateParamsFromState() {
        this.urlParams = explodeParamsString(window.location.search);
        const {currentPage} = this.state;
        const queryState = {currentPage};
        const queryString = implodeParamsObject({...this.urlParams, ...queryState});

        window.history.pushState({}, '', `${window.location.pathname}${queryString}`);
    }

    getCanonicalHrefQueryString(currentPage) {
        const firstPage = 1;
        if(currentPage === firstPage) {
            return '';
        }

        const queryState = {currentPage};
        return implodeParamsObject({...queryState});
    }

    updateCanonicalHref() {
        const canonicalTag = document.querySelector('[rel=canonical]');
        const {currentPage} = this.state;
        if(!canonicalTag || !currentPage) {
            return;
        }

        const queryString = this.getCanonicalHrefQueryString(currentPage);
        const url = window.location.href.split('?')[0];

        canonicalTag.setAttribute('href', `${url}${queryString}`);
    }

    /**
     * @description Hide if need first or last arrow(s) in the Pager.
     */
    // eslint-disable-next-line complexity
    hideFirstLastArrowPager() {
        if(!this.domPagerWrapper) {
            return;
        }

        const next = this.domPagerWrapper.querySelector('[data-pager-next]');
        const previous = this.domPagerWrapper.querySelector('[data-pager-prev]');

        this.state.currentPage === 1 && previous.classList.add(hiddenClass);

        if(this.state.currentPage > 1 && previous.classList.contains(hiddenClass)) {
            previous.classList.remove(hiddenClass);
        }

        this.state.currentPage === this.state.numberOfPages && next.classList.add(hiddenClass);

        if(this.state.currentPage < this.state.numberOfPages && next.classList.contains(hiddenClass)) {
            next.classList.remove(hiddenClass);
        }
    }

    /**
     * @description Update state with url params.
     */
    updateStateFromParams() {
        const urlParamsKeyArray = Object.keys(this.urlParams);
        const stateKeyArray = Object.keys(this.state);
        const keyParamsToStateArray = urlParamsKeyArray.filter(param => stateKeyArray.includes(param));

        const paramsToState = keyParamsToStateArray.reduce((obj, keyParam) => {
            const keyParamValue = /^\d+$/u.test(this.urlParams[keyParam])
                ? parseInt(this.urlParams[keyParam], 10)
                : this.urlParams[keyParam];

            return {...obj, [keyParam]: keyParamValue};
        }, {});

        this.setState(paramsToState);
    }

    /**
     * @description Init Observer in the list to register and unregister entire clickable card function.
     */
    initListObserver() {
        const config = {childList: true};
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const callback = mutationsList => {
            mutationsList.forEach(mutation => {
                if(mutation.type === 'childList') {
                    behaviorClickRegistered(mutation.addedNodes);
                }
            });
        };

        const observer = new MutationObserver(callback);
        observer.observe(this.domListWrapper, config);
    }

    /**
     * @description Init the controller of the paginator.
     */
    initListener() {
        if(this.isInfiniteScroll === 'true') {
            this.initIntersectionObserver();
            this.updateIntersectionObserver();
        } else {
            if(this.domCategoriesList.length > 1) {
                this.initCategoryItemListener();
            }

            if(this.domPagerWrapper) {
                this.initArrowPagerListener();
            }
        }

        this.initListObserver();
    }

    /**
     * @description Init listeners of Pager arrows.
     */
    initArrowPagerListener() {
        this.domPagerWrapper.querySelector('[data-pager-next]')
            .addEventListener('click', () => {
                this.nextPage();
                this.handleClickArrowPager();
            });

        this.domPagerWrapper.querySelector('[data-pager-prev]')
            .addEventListener('click', () => {
                this.previousPage();
                this.handleClickArrowPager();
            });
    }

    initCategoryItemListener() {
        // Dirty fix for IE11
        const eventType = isIE() ? 'mouseup' : 'click';

        this.domCategoriesList.forEach(domItem => {
            domItem.addEventListener(eventType, event => {
                this.updateCategory(event);
            });
        });
    }

    /**
     * @description Callback of click in arrows in the Pager.
     */
    handleClickArrowPager() {
        this.limitFilteredList();
        this.updatePagination();
        this.render();
    }
}
